import type * as React from 'react';
import { useSetRecoilState } from 'recoil';

import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    Heading,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { useAuthOperations, useCurrentUser } from '../../state';
import { useHideSplashscreen } from '../Splashscreen';
import { AccessWall } from './AccessWall';
import { publishedContentCTADismissedAtom } from './OrganizationAccessFallback';

/**
 * Page shown when the user is offered a public version of content they can't access in-app.
 *
 * "Access the published content" opens the public URL via Spine's string-`href` (external nav).
 * "Login to edit" dismisses the CTA for the rest of the session so the next render falls
 * through to the login screen. When there's an authenticated user we also sign them out so the
 * login screen actually shows; when no user is signed in we MUST skip `signOut` — it
 * hard-redirects to "/" which would clobber the URL and break the SAML/continueUrl handoff.
 */
export function PublishedContentCTAPage(props: { publishedURL: string }): React.ReactElement {
    const { publishedURL } = props;
    const { signOut } = useAuthOperations();
    const firebaseUser = useCurrentUser();
    const setDismissed = useSetRecoilState(publishedContentCTADismissedAtom);

    useHideSplashscreen(0);

    const onPressLogin = useEventCallback(async () => {
        setDismissed(true);
        if (firebaseUser) {
            await signOut();
        }
    });

    return (
        <div className="flex min-h-full w-full flex-col bg-base">
            <AccessWall>
                <EmptyState>
                    <EmptyStateIcon>
                        <Icon.Globe />
                    </EmptyStateIcon>
                    <Heading>This content is published online</Heading>
                    <Description>
                        You don't have access to edit this content in GitBook, but you can read the
                        published version. Sign in with a different account if you need to edit.
                    </Description>
                    <EmptyStateActions>
                        <Button
                            kind="primary"
                            href={publishedURL}
                            label="Access the published content"
                        />
                        <Button
                            kind="secondary"
                            onPress={onPressLogin}
                            label="Login to edit the content"
                        />
                    </EmptyStateActions>
                </EmptyState>
            </AccessWall>
        </div>
    );
}
